import {DateTime, nanoid} from "@2d-front-monorepo/core";
import {CustomData} from "./CustomData";
import {EntityHistory} from "./EntityHistory";
import {DriverType} from "./enums/DriverType";
import HolderType from "./enums/HolderType";
import PermitExecutedActions from "./enums/PermitExecutedActions.js";
import PermitStatusType from "./enums/PermitStatusType.js";
import ResidencyUses from "./enums/ResidencyUses";
import VehicleType from "./enums/VehicleType";
import IAllPermits from "./interfaces/IAllPermits";
import ICustomData from "./interfaces/ICustomData";
import {DefaultParkingCategory, ParkingCategory} from "./nominalTypes/ParkingCategory";
import {ParkingType} from "./nominalTypes/ParkingType";
import {PermitId} from "./nominalTypes/PermitId";
import {CreatePermitCommand} from "./types/CreatePermitCommand";
import {GroupedPermit} from "./types/GroupedPermit";
import {PermitResult} from "./types/PermitResult.js";

const CUSTOM_NOTES: string = "custom-notes";
const PARENT_PERMIT_ID_KEY: string = "::parent-permit-id";
const GROUP_NAME_KEY: string = "::group-name";
// const OPERATION_IDS: string = "::operation-breakdown";
const MULTI_OPERATION: string = "::multiple-operations";
const RENEWED: string = "::renewed";
const RENEWED_FROM: string = "::renewed-from";

function sameDate(a: DateTime | undefined, b: DateTime | undefined): boolean {
	return a == null || b == null ? a === b : a.equals(b);
}

/** Mirror of the backend's CanRenewTogether: everything a multi-permit renewal must share.
 *  Driver and category are checked here on top of the backend's list because `renewFromGroup`
 *  copies both from the representative - a mismatch would silently reassign the new permits. */
function canRenewTogether(representative: PermitResult, permit: PermitResult): boolean {
	const a = representative.parkingLocation;
	const b = permit.parkingLocation;

	return representative.tariffId === permit.tariffId
		&& representative.category === permit.category
		&& sameDate(representative.startDate, permit.startDate)
		&& sameDate(representative.endDate, permit.endDate)
		&& a.zoneId === b.zoneId && a.streetSectionId === b.streetSectionId && a.spaceId === b.spaceId
		&& EntityHistory.approvedOrDefault(representative.driver)?.id === EntityHistory.approvedOrDefault(permit.driver)?.id
		&& EntityHistory.approvedOrDefault(representative.residency)?.id === EntityHistory.approvedOrDefault(permit.residency)?.id
		&& representative.payment?.billing?.paymentMethod?.method === permit.payment?.billing?.paymentMethod?.method;
}

class Permits {
	// static children(permits: AllPermitsResult, permit: PermitResult): PermitResult[] {
	// 	const result: PermitResult[] = [];
	// 	for (let child of permits.permits) {
	// 		const parentId = CustomData.getParentPermitId(child);
	// 		if (parentId === permit.id) {
	// 			result.push(child);
	// 		}
	// 	}
	//
	// 	return result;
	// }

	static find(permits: IAllPermits, id: PermitId | undefined): PermitResult | undefined {
		if (id == null) return undefined;
		for (const permit of permits.permits) {
			if (permit.id === id) return permit;
		}
		return undefined;
	}

	static getFilteredPermits(permits: PermitResult[], groupName: string): GroupedPermit[] {
		const result: GroupedPermit[] = [];
		for (const permit of permits) {
			if (groupName === this.getGroupName(permit)) {
				result.push({
					name: groupName,
					startDate: permit.startDate,
					endDate: permit.endDate,
					permit,
					permits: [permit],
					count: 1
				})
			}
		}

		return result;
	}

	static getGroupedPermits(permits: PermitResult[]): GroupedPermit[] {
		const groupMap = new Map<string, GroupedPermit>();
		const groupOrder: string[] = [];

		for (const permit of permits) {
			const groupName = this.getGroupName(permit) ?? nanoid();
			const {startDate, endDate} = permit as { startDate: DateTime; endDate: DateTime };
			const group = groupMap.get(groupName);
			if (group == null) {
				groupMap.set(groupName, {
					name: groupName,
					startDate,
					endDate,
					permit,
					permits: [permit],
					count: 1
				});
				groupOrder.push(groupName);

			} else {
				group.startDate = group.startDate <= startDate ? group.startDate : startDate;
				//an open-ended member (endDate undefined) makes the whole group never-expiring — terminal
				group.endDate = group.endDate == null || endDate == null ? undefined : group.endDate >= endDate ? group.endDate : endDate;
				group.permits.push(permit);
				group.count++;
			}
		}

		const result: GroupedPermit[] = [];
		for (const groupName of groupOrder) {
			result.push(groupMap.get(groupName)!);
		}
		return result;
	}

	static isActive(permit: PermitResult): boolean {
		return permit.status.type === PermitStatusType.Active || permit.status.type === PermitStatusType.AboutToExpire;
	}

	static isAutoRenewEnabled(permit: PermitResult): boolean {
		return (permit.executedActions & PermitExecutedActions.AutoRenew) !== 0;
	}

	/**
	 * Valid state:!(PermitStatusType.Canceled or PermitStatusType.Expired or PermitStatusType.Rejected)
	 * @param permit
	 * @param aboutToExpireIsValid - Use true to let permits in @see {@link Dot: PermitStatusType.AboutToExpire} status to be valid, false otherwise.
	 * @returns
	 */
	static isValidState(permit: PermitResult, aboutToExpireIsValid: boolean): boolean {
		if (aboutToExpireIsValid) {
			return permit.status.type !== PermitStatusType.Cancelled && permit.status.type !== PermitStatusType.Expired && permit.status.type !== PermitStatusType.Rejected;
		}
		return permit.status.type !== PermitStatusType.Cancelled && permit.status.type !== PermitStatusType.AboutToExpire && permit.status.type !== PermitStatusType.Expired && permit.status.type !== PermitStatusType.Rejected;
	}

	static newFormContext(parkingType: ParkingType, permitCategory: ParkingCategory = DefaultParkingCategory): CreatePermitCommand {
		return {
			customData: [],
			tariffId: null!,
			category: permitCategory,
			parkingType: parkingType,
			driver: {
				driverId: null!,
				attachments: {},
				customData: {},
				arguments: {
					fullName: null!,
					attachments: [],
					customData: [],
					type: DriverType.None
				}
			},
			residency: {
				residencyId: null!,
				attachments: {},
				customData: {},
				arguments: {
					address: {},
					uses: ResidencyUses.None,
					zoneId: null!,
					attachments: [],
					customData: []
				}
			},

			vehicles: [[{
				vehicleId: null!,
				attachments: {},
				customData: {},
				arguments: {
					plate: null!,
					type: VehicleType.Resident,
					attachments: [],
					customData: []
				}
			}]],
			permit: {
				arguments: {
					startDate: null!,
					vehicles: [],
					attachments: [],
					customData: [],
					autoRenew: false
				},
				attachments: {},
				customData: {}
			},
			holder: {
				arguments: {
					type: HolderType.Undefined,
					attachments: [],
					customData: []
				},
				attachments: {},
				customData: {}
			}
		}
	}

	static renewFrom(command: CreatePermitCommand, permit: PermitResult): void {
		this.renewFromGroup(command, [permit]);
	}

	/**
	 * Preloads the command with a renewal of `permits`, one vehicle group per permit, in the given
	 * order. A single permit produces a single group, which is what a plain renewal has always been.
	 * Category, tariff, driver, residency and branch come from the representative (`permits[0]`),
	 * which is also the permit `::renewed-from` points at.
	 */
	static renewFromGroup(command: CreatePermitCommand, permits: PermitResult[]): void {
		const representative = permits[0];
		if (representative == null) {
			return;
		}

		command.category = representative.category;
		command.tariffId = representative.tariffId;
		const driverId = EntityHistory.approvedOrDefault(representative.driver)?.id;
		if (driverId != null) {
			command.driver.driverId = driverId;
		}

		const residencyId = EntityHistory.approvedOrDefault(representative.residency)?.id;
		if (residencyId != null) {
			command.residency.residencyId = residencyId;
		}

		command.vehicles.length = permits.length;
		for (let i = 0; i < permits.length; i++) {
			const collection = command.vehicles[i] ??= [];
			collection.length = 0;

			for (const e of EntityHistory.allChanged(permits[i].vehicles)) {
				collection.push({
					arguments: {
						plate: null!,
						type: VehicleType.Resident,
						attachments: [],
						customData: []
					},
					customData: {},
					vehicleId: e.original.id
				})
			}
		}

		command.permit.arguments.branch = representative.parkingLocation;
		// A renewal covers the period AFTER the one expiring: quoting from today would overlap the
		// permit being renewed and Integra rejects the very same plate twice (permit_already_exist).
		// Mirrors the backend's auto-renewal (Api/src/Customers/*/RenewStrategy.cs).
		if (representative.endDate != null) {
			command.permit.arguments.startDate = representative.endDate.addSeconds(1);
		}

		this.setRenewedFrom(command.permit.arguments, representative.id);
	}

	/** The permits sharing this one's `::group-name` - the siblings a multi-permit was broken into.
	 *  Without a group name there is no group, so the permit is alone. */
	static getGroupSiblings(permits: PermitResult[], permit: PermitResult): PermitResult[] {
		const groupName = this.getGroupName(permit);
		if (groupName == null) {
			return [permit];
		}

		return permits.filter(x => this.getGroupName(x) === groupName);
	}

	/**
	 * The subset of a group that can be renewed as one multi-permit: everything the backend's
	 * `CanRenewTogether` (Api/src/Features/Scheduled/AutomaticallyRenewPermits.cs) demands. Keep
	 * both in sync. A candidate must be renewable on its own first - same gate as
	 * {@link RenewComponent}: about to expire and not renewed yet - so a paid-but-not-expiring
	 * sibling is never bundled in and charged. The first candidate is the representative; the rest
	 * must match it on dates, tariff, parking location, driver, residency and payment method.
	 * Returns them in their original order (representative first), or empty when nothing is renewable.
	 */
	static getRenewableGroup(permits: PermitResult[]): PermitResult[] {
		const candidates = permits.filter(x => x.status.type === PermitStatusType.AboutToExpire && !this.alreadyRenewed(x));
		const representative = candidates[0];
		if (representative == null) {
			return [];
		}

		return candidates.filter(x => canRenewTogether(representative, x));
	}

	static getParentPermitId(customData: ICustomData): PermitId | undefined {
		return CustomData.get(customData, PARENT_PERMIT_ID_KEY) as PermitId;
	}

	static setParentPermitId(customData: ICustomData, parentId: PermitId): void {
		CustomData.set(customData, PARENT_PERMIT_ID_KEY, parentId);
	}

	static isMultiOperation(customData: ICustomData): boolean {
		return CustomData.containsKey(customData, MULTI_OPERATION);
	}

	static alreadyRenewed(customData: ICustomData): boolean {
		return CustomData.containsKey(customData, RENEWED);
	}

	static setRenewedFrom(customData: ICustomData, permitId: PermitId): void {
		CustomData.set(customData, RENEWED_FROM, permitId);
	}

	static isRenewed(customData: ICustomData): boolean {
		return CustomData.containsKey(customData, RENEWED_FROM);
	}

	static getGroupName(customData: ICustomData): string | undefined {
		return CustomData.get(customData, GROUP_NAME_KEY);
	}

	static getNotes(customData: ICustomData): string | undefined {
		return CustomData.get(customData, CUSTOM_NOTES);
	}

	static setNotes(customData: ICustomData, notes: string): void {
		CustomData.set(customData, CUSTOM_NOTES, notes);
	}
}

export {Permits};
